// One shared audio player for "Listen" buttons. A button shows "Listen" and, while playing,
// "Pause" with "0:12 of 1:05". Starting one stops any other. Logs playback_start.
import { logger } from '../lib/log.js';
import { fmtShort } from './dom.js';

const log = logger('record');
let audio = null;
let current = null; // { button, url, durationSec }

function labelOf(button) { return button.querySelector('[data-play-label]') || button; }

function reset(button) {
  if (!button) return;
  button.classList.remove('is-playing');
  labelOf(button).textContent = button.getAttribute('data-idle-label') || 'Listen';
}

function position() {
  const total = current && current.durationSec && isFinite(current.durationSec) ? current.durationSec : (isFinite(audio.duration) ? audio.duration : 0);
  return total ? `${fmtShort(audio.currentTime)} of ${fmtShort(total)}` : fmtShort(audio.currentTime);
}

function ensureAudio() {
  if (audio) return audio;
  audio = new Audio();
  audio.preload = 'auto';
  audio.addEventListener('timeupdate', () => {
    if (!current || audio.paused) return;
    labelOf(current.button).textContent = `Pause (${position()})`;
  });
  audio.addEventListener('ended', () => { const c = current; current = null; if (c) reset(c.button); });
  audio.addEventListener('error', () => {
    const c = current; current = null;
    if (!c) return;
    reset(c.button);
    labelOf(c.button).textContent = 'This file cannot play here';
    log.warn('playback_failed', { where: c.where, code: audio.error && audio.error.code });
  });
  return audio;
}

/** Stop whatever is playing (for example, when recording starts). */
export function stopPlayback() {
  if (audio && !audio.paused) audio.pause();
  if (current) { reset(current.button); current = null; }
}

/** Toggle playback for a button. Call from the click handler (iOS needs the gesture). */
export function togglePlay(button, { url, durationSec, where, promptId }) {
  const a = ensureAudio();
  if (!button.hasAttribute('data-idle-label')) button.setAttribute('data-idle-label', labelOf(button).textContent || 'Listen');
  if (current && current.button === button) {
    if (a.paused) {
      a.play().catch(() => {});
      button.classList.add('is-playing');
      labelOf(button).textContent = `Pause (${position()})`;
    } else {
      // Paused: pressing again carries on from here.
      a.pause();
      button.classList.remove('is-playing');
      labelOf(button).textContent = `Keep listening (${position()})`;
    }
    return;
  }
  stopPlayback();
  current = { button, url, durationSec, where };
  a.src = url;
  button.classList.add('is-playing');
  labelOf(button).textContent = 'Pause';
  log.info('playback_start', { where, promptId });
  a.play().catch((e) => {
    log.warn('playback_failed', { where, errName: e && e.name });
    if (current && current.button === button) { reset(button); labelOf(button).textContent = 'This file cannot play here'; current = null; }
  });
}
